<template>
  <label
    :for="props.name"
    :class="labelClasses"
    :data-cy="props.name ? `ds-label--${props.name}` : 'ds-label'"
  >
    <DSLabelContent
      :label="props.label"
      :required="props.required"
      :tooltip="props.tooltip"
      :hidden-label="props.hiddenLabel"
    >
      <template
        v-if="hasSlotContent(slots, 'prefix')"
        #prefix
      >
        <slot name="prefix" />
      </template>

      <template
        v-if="hasSuffix"
        #suffix
      >
        <DSIcon
          v-if="showsInlineEditIcon"
          icon="pencil"
          :size="DS_ICON_SIZES.Small"
        />
        <slot name="suffix" />
      </template>
    </DSLabelContent>

    <slot />
  </label>
</template>

<script setup lang="ts">
import { computed, type VNode } from "vue";
import { DS_LABEL_CLASSES } from "../constants/classes";
import { DSLabelProps } from "@/src/design_system/form_elements/label/types/props";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";
import { hasSlotContent } from "@/src/shared/utils/slots";
import DSLabelContent from "./DSLabelContent.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { DS_ICON_SIZES } from "@/src/design_system/primitives/icon/types";

const props = withDefaults(defineProps<DSLabelProps>(), {
  name: undefined,
  required: false,
  hasError: false,
  inlineEdit: false,
  tooltip: undefined,
  hiddenLabel: false,
});

/**
 * Emptiness is decided here, not in DSLabelContent: once forwarded, an affix arrives there as a
 * slot outlet that always reports content, so only this component sees the caller's real nodes.
 */
const slots = defineSlots<{
  default?: () => VNode[];
  prefix?: () => VNode[];
  suffix?: () => VNode[];
}>();

const { stateClasses } = useStateClasses(props);
const showsInlineEditIcon = computed(() => props.inlineEdit && !props.disabled);
const hasSuffix = computed(() => showsInlineEditIcon.value || hasSlotContent(slots, "suffix"));

const labelClasses = computed(() => ({
  [DS_LABEL_CLASSES.Root]: true,
  [DS_STATE_CLASSES.InlineEdit]: props.inlineEdit,
  [DS_LABEL_CLASSES.WithTooltip]: Boolean(props.tooltip?.trim()),
  ...stateClasses.value,
}));
</script>
